import {
    CircleCheck,
    RotateCcw,
    UserRoundMinus,
    UserRoundPlus,
} from 'lucide-react';
import { type ReactElement } from 'react';

import { formatTime } from '@apps/web/lib/date_utils';
import { type OrganizationConversationAuditEvent } from '@apps/web/swr_data_fetching/conversation_ownership';

type AuditTimelineItemProps = {
    readonly event: OrganizationConversationAuditEvent;
    readonly timezone: string;
};

// Builds the human sentence for a lifecycle/ownership audit event. A null
// actor means the system acted (e.g. an inbound message auto-reopening a
// closed conversation), rendered as "Phoebe".
export function auditEventSentence(
    event: OrganizationConversationAuditEvent,
): string {
    const actor = event.actor_user_name ?? 'Phoebe';
    const target = event.target_user_name ?? 'someone';
    switch (event.event_type) {
        case 'assigned':
            return `${actor} assigned this conversation to ${target}`;
        case 'unassigned':
            return `${actor} unassigned this conversation`;
        case 'closed':
            return `${actor} closed the conversation`;
        case 'reopened':
            return event.actor_user_name
                ? `${event.actor_user_name} reopened the conversation`
                : 'Reopened automatically';
        default: {
            const exhaustiveCheck: never = event.event_type;
            return exhaustiveCheck;
        }
    }
}

function auditEventIcon(
    eventType: OrganizationConversationAuditEvent['event_type'],
): ReactElement {
    switch (eventType) {
        case 'assigned':
            return <UserRoundPlus className="h-3.5 w-3.5" />;
        case 'unassigned':
            return <UserRoundMinus className="h-3.5 w-3.5" />;
        case 'closed':
            return <CircleCheck className="h-3.5 w-3.5" />;
        case 'reopened':
            return <RotateCcw className="h-3.5 w-3.5" />;
        default: {
            const exhaustiveCheck: never = eventType;
            return exhaustiveCheck;
        }
    }
}

// Compact, centered timeline event for lifecycle/ownership changes, styled to
// read as a lightweight system line alongside messages and call cards.
export function AuditTimelineItem({
    event,
    timezone,
}: AuditTimelineItemProps): ReactElement {
    return (
        <div className="my-3 flex items-center justify-center">
            <div className="text-muted-foreground inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium">
                {auditEventIcon(event.event_type)}
                <span>{auditEventSentence(event)}</span>
                <span className="text-gray-400">·</span>
                <span className="tabular-nums">
                    {formatTime(event.created_at, timezone)}
                </span>
            </div>
        </div>
    );
}
